import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MasterDetailService, MasterDetailState } from '../../../screen-engine/services/master-detail.service';
import { BuilderScreenBetaService } from '../../services/builder-screen.service';
import { BuilderNode } from '../../interfaces/builder-screen.models';
import { DataSyncService } from '../../../screen-engine/services/data-sync.service';
import { FormSyncService } from '../../services/form-sync.service';

@Component({
  selector: 'app-master-detail-panel',
  templateUrl: './master-detail-panel.component.html',
  styleUrls: ['./master-detail-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MasterDetailPanelComponent implements OnInit, OnDestroy {
  /**
   * ID do panel de formulário que será exibido
   */
  @Input() formPanelId: string | null = null;

  /**
   * Modo de exibição: 'slide' (lateral) ou 'sheet' (bottom)
   */
  @Input() mode: 'slide' | 'sheet' = 'slide';

  /**
   * Auto-abrir ao selecionar linha
   */
  @Input() autoOpen = true;

  /**
   * Largura padrão do panel (porcentagem)
   */
  @Input() defaultWidth = 40;

  /**
   * Evento de fechamento
   */
  @Output() closed = new EventEmitter<void>();

  /**
   * Estado do panel
   */
  isOpen = false;
  isPinned = false;
  isMinimized = false;

  /**
   * Largura atual do panel (%)
   */
  currentWidth = 40;

  /**
   * Dados da linha selecionada
   */
  selectedRowData: any = null;

  /**
   * ID da grid de origem
   */
  sourceGridId: string | null = null;

  /**
   * Campos do formulário
   */
  formFields: BuilderNode[] = [];

  /**
   * Título do panel
   */
  panelTitle = 'Editar Registro';

  /**
   * Subscription do MasterDetailService
   */
  private subscription?: Subscription;

  /**
   * Subscription do currentSelection (DataSync): atualiza selectedRowData quando o item é atualizado (ex: edição na célula da grade)
   */
  private selectionSyncSubscription?: Subscription;

  /** Última seleção conhecida (path + index) para só atualizar quando mudar e evitar fillFormFields em massa */
  private lastSelectionPath: string | null = null;
  private lastSelectionIndex = -1;
  /** Timeout do debounce de fillFormFields */
  private fillFormFieldsTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Hash dos dados preenchidos para evitar re-fill idêntico */
  private lastFilledDataHash: string | null = null;

  /**
   * Estado do arrastar divisor
   */
  isResizing = false;
  startX = 0;
  startWidth = 0;

  constructor(
    private masterDetailService: MasterDetailService,
    private builderService: BuilderScreenBetaService,
    private dataSyncService: DataSyncService,
    private formSyncService: FormSyncService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * CORREÇÃO: Método centralizado para debounce de fillFormFields
   * Evita chamadas excessivas e verifica se os dados mudaram antes de preencher
   */
  private debouncedFillFormFields(delayMs: number = 100): void {
    if (this.fillFormFieldsTimeout) {
      clearTimeout(this.fillFormFieldsTimeout);
    }
    this.fillFormFieldsTimeout = setTimeout(() => {
      this.fillFormFieldsTimeout = null;

      // Evitar re-fill se dados são idênticos
      const dataHash = JSON.stringify(this.selectedRowData);
      if (this.lastFilledDataHash === dataHash) {
        return;
      }
      this.lastFilledDataHash = dataHash;

      this.fillFormFields();
    }, delayMs);
  }

  ngOnInit(): void {
    this.currentWidth = this.defaultWidth;

    // Escutar mudanças de seleção
    this.subscription = this.masterDetailService.selectedRow$.subscribe(
      (state: MasterDetailState) => {
        this.handleSelectionChange(state);
      }
    );

    // Quando o item da seleção atual muda (ex: clicar em linha da grade filha ou edição na célula), atualizar dados e preencher formulário
    // CORREÇÃO: Também atualizar quando o item muda (mesmo que path/index sejam iguais) - necessário para edição de célula
    this.selectionSyncSubscription = this.dataSyncService.currentSelection$.subscribe((sel) => {
      if (!this.isOpen || !sel?.item) return;
      
      // CORREÇÃO: Não preencher formulário durante edição de célula
      // O valor já foi atualizado localmente - preencher causaria sobrescrita
      if (this.dataSyncService.isCellEditing) {
        // CORREÇÃO: NÃO atualizar selectedRowData durante edição de célula.
        // A sincronização do campo editado é feita via fieldSync$.
        // Atualizar selectedRowData aqui causaria problemas quando fillFormFields
        // for chamado depois (com dados potencialmente incompletos/incorretos).
        return;
      }

      // CORREÇÃO: NÃO re-preencher formulário quando a mudança veio do próprio formulário.
      // Quando o usuário edita um campo (ex: "Título"), updateCurrentItemField cria um
      // novo objeto updatedItem ({...effectiveItem}) e emite currentSelectionSubject.next().
      // Sem esta guarda, fillFormFields re-emite fieldSync$ para TODOS os campos (incluindo
      // campos de pesquisa com dependências como CODECONTRACT), causando:
      //   1. Componentes search (nz-select com nzServerSearch) re-dispararem (nzOnSearch)
      //      ao terem seu valor re-setado programaticamente
      //   2. Dependências de search recalcularem updateDependencyState desnecessariamente
      // O formulário já está atualizado pelo próprio usuário — re-preenchê-lo é redundante.
      if (this.dataSyncService.isUpdatingFromForm) {
        // Ainda assim atualizar selectedRowData para manter referência atualizada
        this.selectedRowData = sel.item;
        return;
      }

      // CORREÇÃO: NÃO processar seleções de grades FILHAS neste painel principal.
      // Quando uma linha é adicionada/selecionada em uma aba filha (ex: Canais, Identificação),
      // currentSelectionSubject emite com arrayPath aninhado (ex: "response.magalu.channels").
      // O item da grade filha NÃO contém campos da grade principal (CODECOMPANY, CODECONTRACT).
      // Sem esta guarda, fillFormFields emite null para esses campos → limpa os campos de pesquisa.
      const mainSelection = this.dataSyncService.getMainSelection();
      if (sel.arrayPath && mainSelection?.arrayPath &&
          sel.arrayPath !== mainSelection.arrayPath &&
          sel.arrayPath.startsWith(mainSelection.arrayPath + '.')) {
        return; // Seleção de grade filha — ignorar no painel principal
      }
      
      const path = sel.arrayPath ?? null;
      const idx = sel.index ?? -1;
      const selectionChanged = path !== this.lastSelectionPath || idx !== this.lastSelectionIndex;
      
      // Verificar se o item mudou (comparação por referência - nova referência = item atualizado)
      const itemChanged = sel.item !== this.selectedRowData;
      
      // Atualizar se seleção ou item mudaram
      if (!selectionChanged && !itemChanged) return;
      
      this.lastSelectionPath = path;
      this.lastSelectionIndex = idx;
      this.selectedRowData = sel.item;
      this.cdr.markForCheck();
      if (this.formFields.length > 0) {
        this.debouncedFillFormFields(80);
      }
    });

    // Carregar campos do formulário
    this.loadFormFields();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.selectionSyncSubscription?.unsubscribe();
    if (this.fillFormFieldsTimeout != null) {
      clearTimeout(this.fillFormFieldsTimeout);
      this.fillFormFieldsTimeout = null;
    }
  }

  /**
   * Lida com mudança de seleção
   */
  private handleSelectionChange(state: MasterDetailState): void {
    const targetMatches = state.targetPanelId === this.formPanelId || 
                          (!state.targetPanelId && !this.formPanelId);

    if (state.selectedRow && targetMatches) {
      // ✅ VERIFICAR se o painel existe no DOM antes de tentar preencher
      const panelElement = document.querySelector('.master-detail-panel');
      if (!panelElement) {
        // Painel não existe no DOM - não fazer nada (silenciosamente)
        return;
      }

      this.selectedRowData = state.selectedRow;
      this.sourceGridId = state.sourceGridId;

      const rowId = state.selectedRow.id || state.selectedRow.item_id || state.selectedRow.item_sku || 'Novo';
      this.panelTitle = `✏️ Editar Registro #${rowId}`;

      this.loadFormFields();

      if (this.formFields.length === 0 && this.sourceGridId) {
        this.generateVirtualFieldsFromGrid();
      }

      if ((this.autoOpen || state.forceOpen) && !this.isOpen) {
        this.open();
      }

      this.cdr.markForCheck();

      if (this.formFields.length > 0) {
        this.debouncedFillFormFields(200); // Reduzido de 500ms
      }
    }
  }

  /**
   * Carrega campos do formulário do builder
   */
  private loadFormFields(): void {
    if (!this.formPanelId) {
      return;
    }

    const allNodes = this.builderService.currentState.nodes;
    const idsUnderPanel = this.builderService.getNodeIdsUnderPanel(allNodes, this.formPanelId);
    this.formFields = allNodes.filter(
      node => this.isFormField(node.type) && idsUnderPanel.has(node.id)
    );

    this.cdr.markForCheck();
  }

  /**
   *  NOVO: Gera campos virtuais a partir das colunas da grid
   * Usado quando Master-Detail está ativo mas não há formulários no canvas
   */
  private generateVirtualFieldsFromGrid(): void {
    if (!this.sourceGridId) {
      return;
    }

    // Buscar a grid nos nodes do builder
    const allNodes = this.builderService.currentState.nodes;
    const gridNode = allNodes.find(node => {
      const gridField = (node.props as any)?.field;
      return gridField === this.sourceGridId || node.id === this.sourceGridId;
    });

    if (!gridNode) {
      return;
    }

    // Extrair colunas da grid
    const columns = (gridNode.props as any)?.columns || [];
    
    if (columns.length === 0) {
      return;
    }


    // Gerar BuilderNodes virtuais para cada coluna
    this.formFields = columns.map((column: any, index: number) => {
      // Pular coluna 'id' se for a primeira
      if (column.field === 'id' && index === 0) {
        return null;
      }

      // Determinar tipo de campo baseado no tipo da coluna
      const fieldType = this.mapColumnTypeToFieldType(column.type);

      // Criar node virtual
      const virtualNode: BuilderNode = {
        id: `virtual_field_${column.field}_${index}`,
        type: fieldType as any, // Cast para permitir tipos dinâmicos
        parentId: null,
        tabId: null,
        props: {
          label: column.header || this.formatLabel(column.field),
          placeholder: `Digite ${column.header || column.field}...`,
          dataPath: column.field, //  Usar field da coluna para mapping
          required: false,
          disabled: false,
          colSpan: fieldType === 'textarea' ? 24 : 12
        } as any // Cast para permitir propriedades customizadas
      };

      return virtualNode;
    }).filter(node => node !== null) as BuilderNode[];

    this.cdr.markForCheck();
  }

  /**
   * Mapeia tipo de coluna para tipo de campo de formulário
   */
  private mapColumnTypeToFieldType(columnType: string): string {
    const typeMap: { [key: string]: string } = {
      'text': 'text',
      'number': 'number',
      'date': 'date',
      'datetime': 'date',
      'boolean': 'switch',
      'select': 'select',
      'default': 'text'
    };

    return typeMap[columnType] || 'text';
  }

  /**
   * Formata label (capitaliza, substitui underscore por espaço)
   */
  private formatLabel(field: string): string {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Verifica se o tipo é um campo de formulário
   */
  private isFormField(type: string): boolean {
    const formTypes = [
      'text', 'textarea', 'number', 'date',
      'select', 'checkbox', 'switch', 'radio'
    ];
    return formTypes.includes(type);
  }

  /**
   * Abre o panel
   */
  open(): void {
    this.isOpen = true;
    this.isMinimized = false;
    this.cdr.markForCheck();

    // Tentar preencher campos após o painel abrir e animar
    this.debouncedFillFormFields(300); // Reduzido de 700ms
  }

  /**
   * Fecha o panel
   */
  close(): void {
    this.isOpen = false;
    this.isMinimized = false;
    
    // Limpar seleção se não estiver fixado
    if (!this.isPinned) {
      this.masterDetailService.clearSelection();
    }

    this.closed.emit();
    this.cdr.markForCheck();
  }

  /**
   * Alterna estado de fixado
   */
  togglePin(): void {
    this.isPinned = !this.isPinned;
    this.cdr.markForCheck();
  }

  /**
   * Minimiza/Maximiza (para modo sheet)
   */
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    this.cdr.markForCheck();
  }

  /**
   * Inicia o resize do panel
   */
  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.currentWidth;

    // Adicionar listeners globais
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  /**
   * Durante o resize
   */
  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isResizing) return;

    const containerWidth = window.innerWidth;
    const deltaX = this.startX - event.clientX; // Invertido porque painel vem da direita
    const deltaPercent = (deltaX / containerWidth) * 100;
    const newWidth = Math.min(Math.max(20, this.startWidth + deltaPercent), 80);

    this.currentWidth = newWidth;
    this.cdr.markForCheck();
  };

  /**
   * Finaliza o resize
   */
  private onMouseUp = (): void => {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  /**
   * Salva as alterações (placeholder)
   */
  save(): void {
    // TODO: Implementar lógica de save
    this.close();
  }

  /**
   * Cancela a edição
   */
  cancel(): void {
    this.close();
  }

  /**
   *  REFATORADO: Preenche os campos do formulário com os dados da linha selecionada
   * Usa FormSyncService para comunicação reativa + manipulação DOM como fallback
   */
  private fillFormFields(): void {
    if (!this.selectedRowData || this.formFields.length === 0) {
      console.error('❌ Não é possível preencher campos:', {
        temDados: !!this.selectedRowData,
        quantidadeCampos: this.formFields.length
      });
      return;
    }

    // Coletar todos os valores para atualização em lote via FormSyncService
    const fieldValues = new Map<string, any>();
    const panelIds = this.formPanelId ? [this.formPanelId] : [];

    // CORREÇÃO: Criar Set de targetNodeIds para este painel específico.
    // Isso garante que apenas campos DENTRO deste painel recebam a atualização,
    // sem afetar campos com mesmo dataPath em outros painéis.
    const targetNodeIds = new Set(this.formFields.map(f => f.id));

    this.formFields.forEach(field => {
      const fieldName = this.extractFieldName(field);
      const value = this.getValueByPath(this.selectedRowData, fieldName);

      if (value !== undefined && value !== null) {
        // Adicionar ao Map para FormSyncService
        fieldValues.set(field.id, value);
        
        // CORREÇÃO: Usar emitFieldSync em vez de setFieldValue (DOM).
        // setFieldValue usa manipulação DOM (input.value + dispatchEvent) que causa cascata:
        //   DOM input event → webhook-adapter valueChanges → onFormFieldChange → store update → getData$ → loop
        // emitFieldSync usa FormControl.setValue com emitEvent:false, sem cascata de eventos.
        this.dataSyncService.emitFieldSync(fieldName, value, 'master_detail_fill', targetNodeIds);
      } else {
        // CORREÇÃO: Também emitir para valores null/undefined para LIMPAR campos que
        // retinham valores da linha anterior (ex: campos de pesquisa como Empresa, Produto, Contrato).
        // Quando uma nova linha vazia é adicionada, esses campos devem ser limpos.
        // targetNodeIds garante que apenas campos DESTE painel são afetados.
        this.dataSyncService.emitFieldSync(fieldName, null, 'master_detail_fill', targetNodeIds);
      }
    });

    // Notificar FormSyncService com todos os valores de uma vez
    if (fieldValues.size > 0) {
      this.formSyncService.setFieldValues(fieldValues, 'selection', panelIds);
    }
  }

  /**
   * Extrai o nome do campo para mapear com os dados
   *  PÚBLICO para uso no template
   */
  extractFieldName(field: BuilderNode): string {
    const props = field.props as any;
    
    //  CORRIGIDO: Priorizar dataPath sobre field
    // dataPath contém o nome REAL do campo nos dados (ex: "item_sku")
    // field contém o ID único gerado (ex: "form_field_item_list_item_sku_0_0")
    const fieldName = props?.dataPath || props?.field || props?.label?.toLowerCase().replace(/\s+/g, '_') || field.id;
    
    return fieldName;
  }

  /**
   * Obtém valor de um objeto usando path (suporta caminhos aninhados)
   */
  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;

    // Se path não tem pontos, acesso direto
    if (!path.includes('.')) {
      return obj[path];
    }

    // Path aninhado: dividir e navegar
    const keys = path.split('.');
    let value = obj;

    for (const key of keys) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[key];
    }

    return value;
  }

  /**
   * Define o valor de um campo
   */
  private setFieldValue(fieldId: string, value: any): void {
    // Procurar dentro do painel, não no documento inteiro
    const panelElement = document.querySelector('.master-detail-panel');
    if (!panelElement) {
      // Painel não está no DOM - ignorar silenciosamente
      return;
    }

    const container = panelElement.querySelector(`[data-component-id="${fieldId}"]`);
    
    if (!container) {
      return;
    }

    // Procurar input dentro do container (funciona para input direto e nz-input-number)
    let inputElement = container.querySelector('input') as HTMLInputElement;
    
    if (!inputElement && container instanceof HTMLInputElement) {
      inputElement = container;
    }

    let textareaElement = container.querySelector('textarea') as HTMLTextAreaElement;
    if (!textareaElement && container instanceof HTMLTextAreaElement) {
      textareaElement = container;
    }

    const switchElement = container.querySelector('nz-switch');
    const selectElement = container.querySelector('nz-select');
    
    if (inputElement) {
      const inputType = inputElement.type;
      
      if (inputType === 'checkbox' || inputType === 'radio') {
        inputElement.checked = !!value;
      } else if (inputType === 'number') {
        // ✅ CORREÇÃO: Para nz-input-number, se o valor for undefined/null, setar null ao invés de tentar converter
        if (value === undefined || value === null || value === '') {
          // Não setar nada, deixar o campo vazio
          inputElement.value = '';
          
          // Para o componente Angular, setar null
          const numberWrapper = inputElement.closest('nz-input-number');
          if (numberWrapper) {
            // Setar null explicitamente para evitar erro de toString
            (numberWrapper as any).ngModel = null;
          }
        } else {
          // Valor válido, converter normalmente
          const numValue = value.toString();
          inputElement.value = numValue;
          
          const numberWrapper = inputElement.closest('nz-input-number');
          if (numberWrapper) {
            (numberWrapper as any).ngModel = parseFloat(numValue) || 0;
          }
        }
      } else {
        inputElement.value = value?.toString() || '';
      }
      
      // Disparar eventos para notificar Angular
      inputElement.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      inputElement.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
      inputElement.dispatchEvent(new KeyboardEvent('keydown', { bubbles: true }));
      inputElement.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      
      // Remover classes de erro agressivamente
      this.removeFormErrorClasses(inputElement);
      setTimeout(() => this.removeFormErrorClasses(inputElement), 50);
      setTimeout(() => this.removeFormErrorClasses(inputElement), 150);
      setTimeout(() => this.removeFormErrorClasses(inputElement), 300);
      
    } else if (textareaElement) {
      textareaElement.value = value?.toString() || '';
      textareaElement.dispatchEvent(new Event('input', { bubbles: true }));
      textareaElement.dispatchEvent(new Event('change', { bubbles: true }));
      
      this.removeFormErrorClasses(textareaElement);
      setTimeout(() => this.removeFormErrorClasses(textareaElement), 50);
      
    } else if (switchElement) {
      const button = switchElement.querySelector('button');
      if (button) {
        const isChecked = switchElement.classList.contains('ant-switch-checked');
        const shouldBeChecked = !!value;
        
        if (isChecked !== shouldBeChecked) {
          button.click();
        }
      }
      
    } else if (selectElement) {
      // Select detectado, mas não implementado
    }
  }

  /**
   * Remove classes de erro do Angular Forms
   */
  private removeFormErrorClasses(element: HTMLElement): void {
    element.classList.remove('ng-invalid', 'ng-touched', 'ng-dirty', 'ng-pending');
    element.classList.add('ng-valid', 'ng-untouched', 'ng-pristine');
    
    element.removeAttribute('aria-invalid');
    element.removeAttribute('aria-describedby');
    
    const formControl = element.closest('.ant-form-item-control');
    if (formControl) {
      formControl.classList.remove('ant-form-item-has-error', 'ant-form-item-has-warning');
      formControl.classList.add('ant-form-item-has-success');
      
      const errorMsgs = formControl.querySelectorAll('.ant-form-item-explain, .ant-form-item-explain-error');
      errorMsgs.forEach(msg => msg.remove());
    }
    
    const formItem = element.closest('.ant-form-item');
    if (formItem) {
      formItem.classList.remove('ant-form-item-has-error', 'ant-form-item-has-warning');
      formItem.classList.add('ant-form-item-has-success');
    }
    
    const numberWrapper = element.closest('.ant-input-number');
    if (numberWrapper) {
      numberWrapper.classList.remove('ant-input-number-status-error');
      numberWrapper.classList.add('ant-input-number-status-success');
    }
    
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      element.style.removeProperty('color');
      element.style.removeProperty('border-color');
      element.style.removeProperty('background-color');
      
      element.style.setProperty('color', 'rgba(0, 0, 0, 0.85)', 'important');
      element.style.setProperty('border-color', '#d9d9d9', 'important');
    }
  }

  /**
   *  NOVO: Helpers para acessar propriedades de campos no template
   */
  getFieldLabel(field: BuilderNode): string {
    return (field.props as any)?.label || field.id;
  }

  getFieldPlaceholder(field: BuilderNode): string {
    return (field.props as any)?.placeholder || '';
  }

  getFieldProp(field: BuilderNode, propName: string): any {
    return (field.props as any)?.[propName];
  }

  /**
   *  NOVO: Manipula mudanças nos campos do formulário
   * Sincroniza com DataSyncService para atualizar JSON global
   */
  onFieldChange(field: BuilderNode, newValue: any): void {
    if (!this.selectedRowData) {
      return;
    }

    const fieldName = this.extractFieldName(field);

    // Atualizar dados locais
    this.setValueByPath(this.selectedRowData, fieldName, newValue);

    // Usar arrayPath da seleção atual (DataSync) para refletir a aba/grade em que o usuário está editando.
    // Caso contrário, ao editar em grade filha (ex: canais), state.arrayPath viria só da grade pai (ex: response.magalu).
    const currentSelection = this.dataSyncService.getCurrentSelection();
    const state = this.masterDetailService.getCurrentState();
    const formContextArrayPath = (currentSelection?.arrayPath && currentSelection.index >= 0)
      ? currentSelection.arrayPath
      : (state?.arrayPath ?? undefined);
    this.dataSyncService.onFormFieldChange(fieldName, newValue, formContextArrayPath);
    
    // CORREÇÃO: Sincronizar outros campos com mesmo dataPath
    // Isso garante que campos duplicados (ex: dentro de Row) sejam atualizados
    this.syncOtherFieldsWithSameDataPath(field.id, fieldName, newValue);
  }
  
  /**
   * Sincroniza outros campos que têm o mesmo dataPath
   * Usado quando um campo é editado para manter campos duplicados em sincronia
   */
  private syncOtherFieldsWithSameDataPath(sourceFieldId: string, dataPath: string, value: any): void {
    if (!this.builderService) return;
    
    const allNodes = this.builderService.currentState.nodes;
    
    // Tipos de campos de formulário que devemos sincronizar
    const formFieldTypes = ['text', 'textarea', 'number', 'switch', 'dropdown', 'select', 'date', 'search'];
    
    // Encontrar todos os campos com o mesmo dataPath (exceto o que originou a mudança)
    const otherFieldsWithSamePath = allNodes.filter(node => {
      if (node.id === sourceFieldId) return false; // Ignorar o campo que originou
      if (!formFieldTypes.includes(node.type)) return false;
      
      const nodeDataPath = (node.props as any)?.dataPath;
      return nodeDataPath === dataPath;
    });
    
    if (otherFieldsWithSamePath.length === 0) return;
    
    // Atualizar cada campo com o novo valor usando DOM diretamente
    otherFieldsWithSamePath.forEach(field => {
      const element = document.querySelector(`[data-component-id="${field.id}"]`) as HTMLInputElement;
      if (element) {
        // Atualizar valor do elemento
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          element.value = value ?? '';
        }
        // Disparar evento de input para atualizar ngModel
        element.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        // Tentar encontrar por outros seletores
        const altElement = document.querySelector(`input[id*="${field.id}"]`) as HTMLInputElement;
        if (altElement) {
          altElement.value = value ?? '';
          altElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  }

  /**
   *  NOVO: Define valor em path específico (helper para onFieldChange)
   */
  private setValueByPath(obj: any, path: string, value: any): void {
    if (!path || !obj) return;

    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  /**
   *  NOVO: Obtém o valor do campo para binding
   * Retorna valor seguro baseado no tipo do campo
   */
  getFieldValue(field: BuilderNode): any {
    if (!this.selectedRowData) {
      return this.getDefaultValueForFieldType(field.type);
    }
    
    const fieldName = this.extractFieldName(field);
    const value = this.getValueByPath(this.selectedRowData, fieldName);
    
    // Se valor é undefined ou null, retornar valor padrão baseado no tipo
    if (value === undefined || value === null) {
      return this.getDefaultValueForFieldType(field.type);
    }
    
    return value;
  }

  /**
   *  NOVO: Retorna valor padrão seguro baseado no tipo do campo
   */
  private getDefaultValueForFieldType(fieldType: string): any {
    switch (fieldType) {
      case 'number':
        return null; // nz-input-number aceita null mas não undefined
      case 'switch':
        return false;
      case 'checkbox':
        return false;
      case 'date':
        return null;
      case 'select':
        return null;
      default:
        return ''; // text, textarea
    }
  }
}

